import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { AppState, RouteConstants } from 'src/app/models';
import { UserInfo } from 'src/app/models/users/user.models';
import { addUser, removeUser } from 'src/app/store/actions/user/user.actions';
import { jwtDecode } from 'jwt-decode';
import { UserClaims } from 'src/app/models/users/user.claims.models';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})

export class AuthHelperService implements OnDestroy {

  loginStartDestroyed$:                                        Subject<void> = new Subject<void>();
  userDestroyed$:                                              Subject<void> = new Subject<void>();
  username =                                                   signal<string>("");
  private loginHint:                                           string | null = null;
  msalWithoutInteractionDestroyed$:                            Subject<void> = new Subject<void>();

  /* 
  * Injections
  */
  private msalBroadcastService=                                inject(MsalBroadcastService);
  private msalService=                                         inject(MsalService);
  private store=                                               inject(Store<AppState>);

  initializeAuthFlow(): void {

    this.listenForLoginSuccess();

    this.listenForInteractionCompletion();
  }

  listenForInteractionCompletion():void{

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .pipe(takeUntil(this.msalWithoutInteractionDestroyed$))
      .subscribe(() => {
    
      const accounts = this.msalService.instance.getAllAccounts();
      const account : AccountInfo | undefined = this.getAccount( accounts , this.loginHint );
      
      if( !account ) {

        this.requestLogin();

        return;
      }

      this.msalService.instance.setActiveAccount(account);

      this.setUserName( account );
      
      this.clearLoginHintFromUrl();
    })
  }


  listenForLoginSuccess():void{
      
    this.msalBroadcastService.msalSubject$.pipe( filter((msg: EventMessage) => {
        
      return msg.eventType === EventType.LOGIN_SUCCESS;
    
    }),takeUntil(this.userDestroyed$),take(1)).subscribe((result: EventMessage) => {

      const payload = result.payload as AuthenticationResult;

      if( payload.account ){
        
        let userInfo : UserInfo =  this.accountInfoToUserInfoMapper( payload.account );

        const accessToken = payload.accessToken;

        const userRoles : string[] = this.getRolesFromAccessToken(accessToken);
        
        userInfo = { ...userInfo , professionalLoggedId : 'first_assignement' , idTokenClaims: { ...userInfo.idTokenClaims , roles: userRoles }  };

        this.store.dispatch(addUser({ ...userInfo }));
        
        this.clearLoginHintFromUrl();
      }
    });
  }

  getAccount( accounts: AccountInfo[], loginHint: string | null ) : AccountInfo | undefined {

    const account : AccountInfo | undefined = 
      loginHint ? 
      accounts.find( acc => acc.username.toLowerCase() === loginHint.toLowerCase() )
      : accounts[0];

    return account;    
  }

  requestLogin():void{
  
    this.msalBroadcastService.msalSubject$.pipe( filter((msg: EventMessage) => {
      
      return msg.eventType === EventType.LOGIN_START;
    
    })).pipe(takeUntil(this.loginStartDestroyed$)).subscribe((result: EventMessage) => {
      
      console.log(result);
    });
  }

  logout() {

    this.store.dispatch(removeUser({}));

    window.localStorage.removeItem('idToken');
    window.localStorage.removeItem('state');
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('username');
    window.localStorage.removeItem('user_id');

    this.msalService.logoutRedirect({

      postLogoutRedirectUri: environment.backendService.logoutRedirectUri
    });
  }

  setUserName( account : AccountInfo ):void{

    const { username } = account;

    this.username.set( username ); 
  }

  getQueryParam(param: string): string | null {
  
    const url = new URL(window.location.href);
    
    const queryParams = new URLSearchParams(url.hash.split('?')[1]);
    
    return queryParams.get(param);
  }

  clearLoginHintFromUrl(): void {

    const url = new URL(window.location.href);
    
    if (url.searchParams.has(RouteConstants.LOGINHINT_PARAM)) {
    
      url.searchParams.delete(RouteConstants.LOGINHINT_PARAM);
    
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  getRolesFromAccessToken(encondedToken:string): string[] {
    
    if (!encondedToken) return [];

    try {

      const decoded : UserClaims = jwtDecode(encondedToken);

      const { roles } = decoded;

      return roles;
    
    } catch (error) {
      
      console.error('Error decoding access token:', error);
      
      return [];
    }
  }

  accountInfoToUserInfoMapper( accountInfo : AccountInfo ):UserInfo{

    const { idToken , homeAccountId , idTokenClaims , localAccountId , name , tenantId , username } = accountInfo;

    const userInfo : UserInfo = {
      
      homeAccountId : homeAccountId,
      idTokenClaims : { ...idTokenClaims },
      localAccountId : localAccountId,
      professionalLoggedId : 'first_assignement',
      name : name,
      tenantId : tenantId,
      username : username,
      idToken : idToken
    }

    return userInfo;
  }

  ngOnDestroy(): void {

    this.userDestroyed$.next();
    this.userDestroyed$.complete();

    this.loginStartDestroyed$.next();
    this.loginStartDestroyed$.complete();

    this.msalWithoutInteractionDestroyed$.next();
    this.msalWithoutInteractionDestroyed$.complete();
  }

}
